import { ReactElement, useState } from "react";
import { DateTime } from "luxon";

import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Guid } from "@/Common/Models/Guid";
import { IconButton, LinkButton } from "@/DesignSystem";
import { FlowsheetClient } from "@/Flowsheets/Api/FlowsheetClient";
import { FlowsheetFilterSettings } from "@/Flowsheets/Models/FlowsheetFilterSettings";

import styles from "./FlowsheetHeader.module.scss";

import GearFillIcon from "@/DesignSystem/Atoms/Icons/Types/Gear/gear-fill.svg";
import PrinterFillIcon from "@/DesignSystem/Atoms/Icons/Types/Printer/printer-fill.svg";

interface IFlowsheetHeaderProps {
    /**
     * The name of the flowsheet
     */
    readonly name: string;

    /**
     * Name of the employee that created the flowsheet
     */
    createdBy: string;

    /**
     * The function to call when the configure flowsheet link is clicked
     */
    onClickConfigureFlowsheet: () => void;

    /**
     * Name of the employee that signed off the flowsheet
     */
    signedOffBy?: string;

    /**
     * Signed off date of the flowsheet
     */
    signedOffDate?: Date;

    /*
    * Date of the encounter
    */
    encounterDate: Date;

    /**
     * Filter settings of the flowsheet
     */
    flowsheetFilterSettings?: FlowsheetFilterSettings

    /*
     * Function to set the filter settings of the flowsheet by component name
     */
    onFilterSelected: (filterName: string) => void;

    /**
     * Unique identifier for the flowsheet.
     */
    flowsheetId: Guid;
}

function FlowsheetHeader(props: Readonly<IFlowsheetHeaderProps>): ReactElement {
    const { getText } = useTranslation();

    const formattedSignedOffDate = props.signedOffDate
        ? DateTime.fromJSDate(props.signedOffDate).toFormat("yyyy-MM-dd @ HH:mm:ss")
        : "";

    const [isPrinting, setIsPrinting] = useState(false);

    return (
        <header className={styles.flowsheetHeader}>
            <div
                className={styles.flowsheetHeaderContainer}
                data-cy="flowsheet-header"
            >
                <div className={styles.flowsheetHeaderTitleAndButtons}>
                    <p
                        className={styles.flowsheetTitle}
                        data-cy="flowsheet-header-title"
                    >
                        {props.name}
                    </p>
                    <ButtonGroup>
                        {cacheStore.ClinicSettings.EnableChronicDiseaseManagementPrintPdf && props.signedOffDate && (
                            <IconButton
                                variant="neutral"
                                category="secondary"
                                disabled={isPrinting}
                                onClick={async () => {
                                    setIsPrinting(true);
                                    const response = await FlowsheetClient.printFlowsheet(props.flowsheetId, props.flowsheetFilterSettings);
                                    if (!response.isSuccess)
                                        sessionStore.showPopup(response.errors);
                                    else if (response.data?.FileName) {
                                        PrintHelper.ShowPrintPreviewFileName(response.data.FileName);
                                    }
                                    setIsPrinting(false);
                                }}
                            >
                                <PrinterFillIcon />
                            </IconButton>
                        )}
                        <IconButton
                            variant="neutral"
                            category="secondary"
                            onClick={() => props.onFilterSelected("")}
                        >
                            <GearFillIcon />
                        </IconButton>
                    </ButtonGroup>
                </div>
                <LinkButton
                    dataCy="flowsheets-configure-flowsheet-button"
                    label={getText("Flowsheets_ConfigureFlowsheetLabel")}
                    underline={false}
                    onClick={() => props.onClickConfigureFlowsheet()}
                    disabled={props.signedOffBy != null}
                />
            </div>
            <div className={styles.flowsheetHeaderBanner}>
                <span>{props.createdBy}</span>
                {props.signedOffBy && (
                    <div className={styles.flowsheetHeaderSignedOff}>
                        <span>
                            {getText("Flowsheets_SignedOffByLabel", { signedOffBy: props.signedOffBy })}
                        </span>
                        <span>{formattedSignedOffDate}</span>
                    </div>
                )}
            </div>
        </header>
    );
}

export { FlowsheetHeader };
